import TeamsOrganizer from "../components/teams/TeamsOrganizer";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ApiService } from "services/ApiService";
import { Team } from "models/teams/Team";
import OperationsHub from "components/operations/OperationsHub";
import { WorkflowOrganizer } from "components/workflows/WorkflowOrganizer";
import { FormOrganizer } from "components/forms/FormOrganizer";
import ArtistBillingSettings from "components/artists/billing/ArtistBillingSettings";
import { utilities } from "utils/utilities";
import { BillingAccount } from "models/billing/BillingAccount";
import { OnboardingState } from "models/billing/OnboardingState";

type ViewMode = "hub" | "operations" | "team" | "blueprints" | "workflows" | "forms" | "billing";

const TeamsPage: React.FC = () => {
    const { artistId, teamId } = useParams<{ artistId: string; teamId: string }>();
    const isArtist = utilities.getUserId() === artistId;
    const [viewMode, setViewMode] = useState<ViewMode>("hub");
    const [team, setTeam] = useState<Team | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [billingAccount, setBillingAccount] = useState<BillingAccount | null>(null);
    const [billingError, setBillingError] = useState<boolean>(false);
    const [billingLoading, setBillingLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchTeam = async () => {
            if (!teamId) {
                setError("Team not found");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                const resp = await ApiService.getTeamAsync(teamId, artistId ?? "");
                if (!resp?.data?.teamId) {
                    setTeam(null);
                    setError("No team found for this teamId");
                    return;
                }
                setTeam(resp.data);
            } catch (e) {
                console.error("Failed to fetch team", e);
                setTeam(null);
                setError("Failed to fetch team");
            } finally {
                setLoading(false);
            }
        };

        void fetchTeam();
    }, [artistId, teamId]);

    useEffect(() => {
        if (!isArtist || !artistId) return;

        const fetchBilling = async () => {
            setBillingLoading(true);
            setBillingError(false);
            try {
                const account = await ApiService.getArtistBillingAccount(artistId);
                setBillingAccount(account);
            } catch (e) {
                console.error("Failed to fetch billing account", e);
                setBillingError(true);
            } finally {
                setBillingLoading(false);
            }
        };

        void fetchBilling();
    }, [isArtist, artistId]);

    const isBillingComplete = billingAccount?.onboardingState === OnboardingState.Complete;
    const billingGateActive = isArtist && !billingLoading && !billingError && !isBillingComplete;
    const restrictedViews: ViewMode[] = ["operations", "team", "blueprints", "workflows", "forms"];

    useEffect(() => {
        if (billingGateActive && restrictedViews.includes(viewMode)) {
            setViewMode("hub");
        }
    }, [billingGateActive, viewMode]);

    if (loading) {
        return <h2 className="text-center mt-8">Loading...</h2>;
    }

    if (error || !team || !artistId || !teamId) {
        return <h2 className="text-center mt-8">{error || "Not found!"}</h2>;
    }

    const backToHub = () => setViewMode("hub");
    const backToBlueprints = () => setViewMode("blueprints");

    if (viewMode === "operations") {
        return (
            <div className="min-h-screen bg-gray-50">
                <button
                    type="button"
                    onClick={backToHub}
                    className="m-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                >
                    ← Back
                </button>
                <OperationsHub team={team} artistId={artistId} />
            </div>
        );
    }

    if (viewMode === "team") {
        return (
            <div className="min-h-screen bg-gray-50">
                <button
                    type="button"
                    onClick={backToHub}
                    className="m-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                >
                    ← Back
                </button>
                <TeamsOrganizer teamId={teamId} />
            </div>
        );
    }

    if (viewMode === "workflows") {
        return (
            <div className="min-h-screen bg-gray-50">
                <button
                    type="button"
                    onClick={backToBlueprints}
                    className="m-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                >
                    ← Back
                </button>
                <WorkflowOrganizer ownerId={teamId} />
            </div>
        );
    }

    if (viewMode === "forms") {
        return (
            <div className="min-h-screen bg-gray-50">
                <button
                    type="button"
                    onClick={backToBlueprints}
                    className="m-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                >
                    ← Back
                </button>
                <FormOrganizer ownerId={teamId} />
            </div>
        );
    }

    if (viewMode === "billing") {
        return (
            <div className="min-h-screen bg-gray-50">
                <button
                    type="button"
                    onClick={backToHub}
                    className="m-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                >
                    ← Back
                </button>
                <ArtistBillingSettings artistId={artistId} />
            </div>
        );
    }

    if (viewMode === "blueprints") {
        return (
            <div className="min-h-screen bg-gray-50 p-8">
                <button
                    type="button"
                    onClick={backToHub}
                    className="mb-6 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                >
                    ← Back
                </button>

                <h1 className="text-3xl font-bold text-center mb-8 text-gray-800">Blue Prints</h1>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                    <div
                        onClick={() => setViewMode("workflows")}
                        className="bg-white border-2 border-gray-200 rounded-lg shadow-lg p-8 cursor-pointer hover:border-blue-500 hover:shadow-xl transition-all"
                    >
                        <div className="text-center">
                            <div className="text-5xl mb-4">🔄</div>
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">Workflows</h2>
                            <p className="text-gray-600">Create and manage workflows</p>
                        </div>
                    </div>

                    <div
                        onClick={() => setViewMode("forms")}
                        className="bg-white border-2 border-gray-200 rounded-lg shadow-lg p-8 cursor-pointer hover:border-blue-500 hover:shadow-xl transition-all"
                    >
                        <div className="text-center">
                            <div className="text-5xl mb-4">📝</div>
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">Forms</h2>
                            <p className="text-gray-600">Design and manage forms</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // hub
    return (
        <div className="min-h-screen bg-gray-50 p-8">
            <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">Team Dashboard</h1>
            <p className="text-center text-gray-600 mb-8">{team.teamName}</p>

            {billingGateActive && (
                <div className="max-w-6xl mx-auto mb-6 p-4 bg-amber-50 border border-amber-300 rounded-lg text-amber-800 text-center">
                    Complete your billing setup to unlock Blueprints, Operations, and Team management.
                </div>
            )}

            {billingError && (
                <div className="max-w-6xl mx-auto mb-6 p-4 bg-red-50 border border-red-300 rounded-lg text-red-800 text-center">
                    Unable to verify billing status.{" "}
                    <button
                        type="button"
                        onClick={() => {
                            setBillingError(false);
                            setBillingLoading(true);
                            ApiService.getArtistBillingAccount(artistId)
                                .then(account => setBillingAccount(account))
                                .catch(e => { console.error(e); setBillingError(true); })
                                .finally(() => setBillingLoading(false));
                        }}
                        className="underline font-semibold hover:text-red-600"
                    >
                        Try again
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                <div
                    onClick={billingGateActive ? undefined : () => setViewMode("operations")}
                    className={`bg-white border-2 rounded-lg shadow-lg p-8 transition-all ${
                        billingGateActive
                            ? "border-gray-100 opacity-40 cursor-not-allowed"
                            : "border-gray-200 cursor-pointer hover:border-blue-500 hover:shadow-xl"
                    }`}
                    title={billingGateActive ? "Complete billing setup to access Operations" : undefined}
                >
                    <div className="text-center">
                        <div className="text-5xl mb-4">⚙️</div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Operations</h2>
                        <p className="text-gray-600">Manage projects and submissions</p>
                    </div>
                </div>

                <div
                    onClick={billingGateActive ? undefined : () => setViewMode("team")}
                    className={`bg-white border-2 rounded-lg shadow-lg p-8 transition-all ${
                        billingGateActive
                            ? "border-gray-100 opacity-40 cursor-not-allowed"
                            : "border-gray-200 cursor-pointer hover:border-blue-500 hover:shadow-xl"
                    }`}
                    title={billingGateActive ? "Complete billing setup to access Team management" : undefined}
                >
                    <div className="text-center">
                        <div className="text-5xl mb-4">👥</div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Team</h2>
                        <p className="text-gray-600">Manage members and settings</p>
                    </div>
                </div>

                <div
                    onClick={billingGateActive ? undefined : () => setViewMode("blueprints")}
                    className={`bg-white border-2 rounded-lg shadow-lg p-8 transition-all ${
                        billingGateActive
                            ? "border-gray-100 opacity-40 cursor-not-allowed"
                            : "border-gray-200 cursor-pointer hover:border-blue-500 hover:shadow-xl"
                    }`}
                    title={billingGateActive ? "Complete billing setup to access Blueprints" : undefined}
                >
                    <div className="text-center">
                        <div className="text-5xl mb-4">📋</div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Blue Prints</h2>
                        <p className="text-gray-600">Workflows and forms</p>
                    </div>
                </div>

                <div
                    onClick={isArtist ? () => setViewMode("billing") : undefined}
                    className={`bg-white border-2 rounded-lg shadow-lg p-8 transition-all ${
                        isArtist
                            ? "border-gray-200 cursor-pointer hover:border-blue-500 hover:shadow-xl"
                            : "border-gray-100 opacity-40 cursor-not-allowed"
                    }`}
                    title={isArtist ? undefined : "Only the artist can access billing"}
                >
                    <div className="text-center">
                        <div className="text-5xl mb-4">💳</div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Billing</h2>
                        <p className="text-gray-600">Manage payouts and onboarding</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TeamsPage;